import React from 'react';
import { BrainCircuit, Layers, Workflow, Check, X, Minus } from 'lucide-react';
import { SlideWrapper } from './SlideWrapper';
import { deckImage, deckVideo } from './deckAssets';

export function SlideTitle({ forClient, forOwner, forPartner }) {
  return (
    <SlideWrapper
      className="items-center justify-center text-center"
      id="title"
      backgroundImage={deckImage('/construction-site.jpg')}
      overlay="gradient"
    >
      <div className="flex flex-col items-center gap-8">
        <img
          src={deckImage('/jobotics-logo.png')}
          alt="Jobotics AI"
          className="h-64 w-auto"
        />
        <p className="max-w-2xl text-xl leading-relaxed text-gray-600 md:text-2xl">
          {forPartner ? 'Partner with Jobotics — Co-sell and extend your platform with our Answer & Action Engine.' : forOwner ? 'One place for your capital program data — visibility, control, and fewer surprises across projects and delivery teams.' : forClient ? 'One place for your project data — ask questions, get answers, without switching tools.' : 'An AI Answer & Action Engine for Construction'}
        </p>
      </div>
    </SlideWrapper>
  );
}

const painStats = [
  { metric: '50+', title: 'Software Tools', desc: 'We don\'t need another new software' },
  { metric: '14', title: 'Hours Lost', desc: 'Per week, per team on fragmented data' },
  { metric: '10%', title: 'Project Costs Wasted', desc: 'Due to inefficient data management' },
  { metric: '92%', title: 'Rework & Data-Driven', desc: 'Caused by poor data & miscommunication' },
];

export function SlideOpportunity({ forClient }) {
  return (
    <SlideWrapper id="opportunity" backgroundImage={deckImage('/construction-site.jpg')} overlay="gradient">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forClient ? 'Why Contractors Choose Us' : 'The Opportunity'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forClient ? 'One Place for Your Data — No More Fragmented Tools' : 'One Trillion Dollar Opportunity'}
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-10 shadow-lg">
            <span className="text-5xl font-bold text-primary-500 md:text-6xl font-heading">$1T+</span>
            <p className="text-base text-gray-600">Annual US construction spending wasted</p>
          </div>
          <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-10 shadow-lg">
            <span className="text-5xl font-bold text-construction-accent md:text-6xl font-heading">30K+</span>
            <p className="text-base text-gray-600">Mid-size MEP companies on fragmented tools</p>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          <h3 className="text-xl font-bold text-gray-900 font-heading">{forClient ? 'Your teams are spending too much time hunting for data' : 'Construction folks are working as firefighters'}</h3>
          <div className="grid gap-6 md:grid-cols-4">
            {painStats.map((stat) => (
              <div key={stat.title} className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                <span className="text-3xl font-bold text-gray-900 font-heading">{stat.metric}</span>
                <span className="text-base font-semibold text-gray-900">{stat.title}</span>
                <span className="text-base text-gray-600">{stat.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

const stories = [
  { role: 'Project Managers', pain: 'Too many tools, no single view. Finding the answer takes hours.', accent: 'text-primary-500' },
  { role: 'Executives', pain: 'No clear view of project status or risk until it’s too late.', accent: 'text-construction-accent' },
  { role: 'Field Workers', pain: 'Need answers on the jobsite but can’t search or pull up data easily.', accent: 'text-emerald-600' },
  { role: 'Financial Controllers', pain: 'Cost and billing data spread across systems — no one source of truth.', accent: 'text-amber-600' },
];
const storiesOwner = [
  { role: 'Capital Program / Owner', pain: 'No single view across projects and contractors. Risk and cost surface too late.', accent: 'text-primary-500' },
  { role: 'PMO', pain: 'Too many tools and delivery teams; no unified reporting or portfolio visibility.', accent: 'text-construction-accent' },
  { role: 'Project Controls', pain: 'Cost and schedule data scattered across projects — no one source of truth.', accent: 'text-emerald-600' },
  { role: 'Delivery Teams', pain: 'Need answers across projects but can’t get them without chasing contractors.', accent: 'text-amber-600' },
];

export function SlideProblem({ forClient, forOwner, forPartner }) {
  return (
    <SlideWrapper id="problem" backgroundImage={deckImage('/bg-blueprint.jpg')} overlay="light">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forPartner ? 'The opportunity' : forOwner ? 'What We Solve for Owners' : forClient ? 'What We Solve for You' : 'The Problem'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forPartner ? 'Your Customers’ Pain — Our Shared Opportunity' : forOwner ? 'Built for Owner & Program Roles — Portfolio to Delivery' : forClient ? 'Sound Familiar? We Built for Every Role — Office & Jobsite' : <>Built for Key Roles in the <span className="text-gray-600">Office & Jobsite</span></>}
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {(forOwner ? storiesOwner : stories).map((story) => (
            <div key={story.role} className="flex items-start gap-5 rounded-2xl border border-gray-200 bg-white p-10 shadow-sm">
              <span className={`text-2xl font-bold font-heading ${story.accent}`}>{story.role.split(' ')[0].slice(0, 1)}</span>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-bold text-gray-900 font-heading">{story.role}</h3>
                <p className="text-base text-gray-600">{story.pain}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

const pillars = [
  { icon: BrainCircuit, label: 'Project Intelligence (Memory)', description: 'Dashboards, AI Answer, and Voice Agent in one unified screen.', color: 'text-primary-500', bg: 'bg-primary-100' },
  { icon: Layers, label: '10+ Core AI Assistants (Skills)', description: 'Project Management, ERP, Safety, CRM — answers in seconds.', color: 'text-construction-accent', bg: 'bg-orange-100' },
  { icon: Workflow, label: 'Functional Intelligence (SOP)', description: 'Finance, Estimation, Contracts — automated end-to-end.', color: 'text-emerald-600', bg: 'bg-emerald-100' },
];
const pillarsClient = [
  { icon: BrainCircuit, label: 'One screen for your projects', description: 'Files, RFIs, submittals, and financials in one place. Ask in plain language, get answers from the tools you already use.', color: 'text-primary-500', bg: 'bg-primary-100' },
  { icon: Layers, label: 'Ask in plain language', description: 'Project management, accounting, safety, CRM — type a question, get an answer from Procore, Vista, or your docs in seconds.', color: 'text-construction-accent', bg: 'bg-orange-100' },
  { icon: Workflow, label: 'Automated workflows', description: 'Estimating, contracts, reporting — tasks that used to take hours run in minutes.', color: 'text-emerald-600', bg: 'bg-emerald-100' },
];
const pillarsOwner = [
  { icon: BrainCircuit, label: 'Portfolio visibility', description: 'One place for all projects and delivery teams. Dashboards, status, and risk in a single view.', color: 'text-primary-500', bg: 'bg-primary-100' },
  { icon: Layers, label: 'Ask across programs', description: 'Ask questions across projects, contractors, and systems — get answers without chasing delivery teams.', color: 'text-construction-accent', bg: 'bg-orange-100' },
  { icon: Workflow, label: 'Control and automation', description: 'Reporting, change management, and workflows — so you see issues early and control outcomes.', color: 'text-emerald-600', bg: 'bg-emerald-100' },
];
const pillarsPartner = [
  { icon: BrainCircuit, label: 'Extend your platform', description: 'Add Jobotics as a layer on top of your product — unified answers across your data and ours.', color: 'text-primary-500', bg: 'bg-primary-100' },
  { icon: Layers, label: 'Co-sell opportunities', description: 'Joint value for shared customers: we bring Answer & Action Engine; you bring domain and distribution.', color: 'text-construction-accent', bg: 'bg-orange-100' },
  { icon: Workflow, label: 'Skill Grid & integrations', description: 'Our Skill Grid covers PM, ERP, field, safety, CRM — we integrate; you focus on your core.', color: 'text-emerald-600', bg: 'bg-emerald-100' },
];

export function SlideSolution({ forClient, forOwner, forPartner }) {
  return (
    <SlideWrapper id="solution" backgroundImage={deckImage('/bg-digital-construction.jpg')} overlay="light">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forPartner ? 'Partnership' : forOwner ? 'What We Deliver' : forClient ? 'What We Deliver' : 'The Platform'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forPartner ? 'Co-sell with Jobotics — One Platform to Add to Your Offering' : forOwner ? 'One Platform for Your Capital Program' : forClient ? 'One Platform for Your Projects' : 'Agentic Platform for Construction'}
          </h2>
          <p className="max-w-3xl text-lg leading-relaxed text-gray-600">
            {forPartner ? 'We bring the Answer & Action Engine and Skill Grid; you bring your product and customers. Together we solve fragmentation and unlock co-sell revenue.' : forOwner ? 'Portfolio visibility and control — so owners and program teams see the same picture across projects and contractors without jumping between systems.' : forClient ? 'One place for your project data, dashboards, and automation — so office and field see the same picture without jumping between systems.' : 'Together, these modules eliminate manual data hunting and give every role — from field workers to executives — instant access to unified project intelligence.'}
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {(forPartner ? pillarsPartner : forOwner ? pillarsOwner : forClient ? pillarsClient : pillars).map((pillar) => {
            const Icon = pillar.icon;
            return (
            <div key={pillar.label} className={`flex flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-10 shadow-sm hover:shadow-md ${pillar.bg}`}>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl">
                <Icon className={`h-7 w-7 ${pillar.color}`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 font-heading">{pillar.label}</h3>
              <p className="text-base leading-relaxed text-gray-600">{pillar.description}</p>
            </div>
            );
          })}
        </div>
      </div>
    </SlideWrapper>
  );
}

const modules = [
  { label: '360 Project Dashboard', description: "One screen showing every project's files, financials, RFIs, submittals, change orders, and safety data from all your systems." },
  { label: 'AI Answer', description: 'Ask one question, get answers from Vista, Procore, Safety, and Documents simultaneously — unified intelligence in a single response.' },
  { label: 'Voice Agent', description: 'Hands-free, voice-powered data queries for the field — ask a question on-site and hear the answer in real time.' },
];
const modulesClient = [
  { label: 'Project dashboard', description: "One screen: every project's files, financials, RFIs, submittals, change orders, and safety — from all your systems." },
  { label: 'Ask in plain language', description: 'Type one question; get answers from Vista, Procore, safety, and your documents in one place.' },
  { label: 'Voice on the jobsite', description: 'Hands-free: ask a question in the field, hear the answer — no typing required.' },
];
const assistants = [
  { name: 'PM Analyst', desc: 'Query Procore, ACC, Fieldwire, SharePoint data conversationally' },
  { name: 'ERP Analyst', desc: 'Ask financial questions in plain English, get real-time answers' },
  { name: 'Safety Analyst', desc: 'Analyze incident reports, near misses, and safety trends' },
  { name: 'CRM Analyst', desc: 'Visualize sales pipeline, win rates, and bid history' },
];
const assistantsClient = [
  { name: 'Projects', desc: 'Ask about Procore, ACC, Fieldwire, SharePoint in plain language' },
  { name: 'Accounting', desc: 'Financial questions answered from your ERP in real time' },
  { name: 'Safety', desc: 'Incident reports, near misses, and safety trends' },
  { name: 'CRM', desc: 'Pipeline, win rates, and bid history' },
];
const functionalTools = [
  { name: 'AI Report', desc: 'Generate CPI/SPI reports in Excel with one click' },
  { name: 'Labor Estimator', desc: 'Look up MEP labor hours by type, size, and connection' },
  { name: 'Material Matrix', desc: 'Generate templated material takeoff lists from specs' },
  { name: 'Contract Review', desc: 'AI-annotated risk highlights and redlines on PDFs' },
];
const functionalToolsClient = [
  { name: 'Reports', desc: 'CPI/SPI and other reports to Excel with one click' },
  { name: 'Labor estimator', desc: 'MEP labor hours by type, size, and connection' },
  { name: 'Material takeoff', desc: 'First-draft material lists from specs' },
  { name: 'Contract review', desc: 'Risk highlights and redlines on contract PDFs' },
];

export function SlideValue({ forClient, forOwner, forPartner }) {
  const useClientContent = forClient || forOwner || forPartner;
  return (
    <SlideWrapper id="value" backgroundImage={deckImage('/bg-steel-structure.jpg')} overlay="light">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forPartner ? 'What we bring to the partnership' : forOwner ? 'What You Get' : forClient ? 'What You Get' : '360° Intelligence'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl font-heading">
            {forPartner ? 'Unified Intelligence + Skill Grid — Your Customers Get More Value' : forOwner ? 'Portfolio at Your Fingertips — One Screen, All Your Projects & Teams' : forClient ? 'Everything at Your Fingertips — One Screen, All Your Systems' : 'Everything at Your Fingertips'}
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {(useClientContent ? modulesClient : modules).map((mod, i) => (
            <div key={mod.label} className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-base font-bold text-white font-heading">{i + 1}</span>
                <h3 className="text-base font-bold text-gray-900 font-heading">{mod.label}</h3>
              </div>
              <p className="text-base leading-relaxed text-gray-600">{mod.description}</p>
            </div>
          ))}
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          <div className="flex flex-col gap-5">
            <h3 className="text-base font-semibold uppercase tracking-widest text-construction-accent">{useClientContent ? 'Ask by topic' : 'Core AI Assistants'}</h3>
            <div className="grid grid-cols-2 gap-4">
              {(useClientContent ? assistantsClient : assistants).map((a) => (
                <div key={a.name} className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-orange-50 p-5">
                  <span className="text-base font-bold text-gray-900">{a.name}</span>
                  <span className="text-base text-gray-600">{a.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-5">
            <h3 className="text-base font-semibold uppercase tracking-widest text-emerald-600">{useClientContent ? 'Built-in tools' : 'Functional Intelligence'}</h3>
            <div className="grid grid-cols-2 gap-4">
              {(useClientContent ? functionalToolsClient : functionalTools).map((t) => (
                <div key={t.name} className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-emerald-50 p-5">
                  <span className="text-base font-bold text-gray-900">{t.name}</span>
                  <span className="text-base text-gray-600">{t.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {forOwner && (
          <div className="rounded-2xl border-2 border-primary-500 bg-primary-50 p-6">
            <h3 className="text-base font-semibold uppercase tracking-widest text-primary-500 mb-3">Highlight for owners</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1 rounded-xl border border-primary-200 bg-white p-4">
                <span className="text-lg font-bold text-primary-600">GC/SC data consolidation</span>
                <p className="text-sm text-gray-700">One view across general contractors and subs — status, cost, and schedule in one place.</p>
              </div>
              <div className="flex flex-col gap-1 rounded-xl border border-primary-200 bg-white p-4">
                <span className="text-lg font-bold text-primary-600">Contextual graph</span>
                <p className="text-sm text-gray-700">Relationships and dependencies at a glance — consolidated insights without digging through tools.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </SlideWrapper>
  );
}

// Case study data (Egan-style 6 benefits + Sylvan-style problem & impact)
export const caseStudyEganBenefits = [
  { num: 1, title: 'Job Close-out', desc: 'Automated gathering of all close-out documentation into a single zip file', metric: '98% Time Reduction', sub: '4 Hours Down to 3 Minutes' },
  { num: 2, title: 'Contract Review / Risk Analysis', desc: 'Automated analysis of large (1,000+ page) contract documents for risk and key terms', metric: '93% Time Reduction', sub: '1 Week to 30 Minutes for Reviewable Draft' },
  { num: 3, title: 'Material Matrix Generation', desc: 'Scrubbing specs against Revit data to create first-draft material lists for VDC', metric: '85% – 95% Time Reduction', sub: '1 Week Down to 2–4 Hours' },
  { num: 4, title: 'Information Search', desc: 'Real-time querying of project data (RFIs, Submittals, Drawings) across systems', metric: '40% – 70% Time Reduction', sub: '5–10 Minutes Down to 3 Minutes Per Search' },
  { num: 5, title: 'Drawing Comparison', desc: 'Compare old vs. new drawings without manual uploads or downloads', metric: 'Eliminates Manual File Handling', sub: 'Compare versions directly from your systems — no PDF juggling' },
  { num: 6, title: 'Operational Scaling', desc: 'Ability to handle higher project volumes without increasing headcount', metric: '2x Productivity', sub: 'Enables Team to Do 2x the Work with Zero Additional Overhead' },
];

export const caseStudySylvanProblem = [
  { metric: '50+', label: 'Software Tools Used Across the Contracting Landscape' },
  { metric: '~14', label: 'Hours Lost Per Week, Per Team On Fragmented Data' },
  { metric: '~10%', label: 'Project Costs Wasted Due to Inefficient Data Management' },
  { metric: '50%+', label: 'Rework is Data-Driven' },
];

export const caseStudySylvanProductivity = [
  { metric: '90%', label: 'Fast Decision Making — Hrs to Minutes' },
  { metric: '5+ Hrs', label: 'Saved Weekly Per PM — Project Estimation, Planning, and Ops' },
  { metric: '16+ Hrs', label: 'Saved Monthly — Automated, Accurate Multi-Source Financial Reporting' },
];

export const caseStudySylvanBusiness = [
  { metric: '$40M', label: 'Revenue Gains — Past Due Receivable Signaling & Collection' },
  { metric: '$3M+', label: 'Productivity Gains — 3–5% Margin Improvement' },
  { metric: '30%', label: 'Faster RFI Resolution — Instant Status Check and Proactive Follow-Up' },
];
// Owner deck: no receivables; keep only number-driven outcomes (GC/SC and contextual graph are features, not case study)
const caseStudySylvanBusinessOwner = [
  { metric: '$3M+', label: 'Productivity Gains — 3–5% Margin Improvement' },
  { metric: '30%', label: 'Faster RFI Resolution — Instant Status Check and Proactive Follow-Up' },
];

// Case study highlight (teaser) — use early in client/owner/partner deck to hook
export function SlideCaseStudyHighlight({ forClient, forOwner, forPartner }) {
  if (!forClient && !forOwner && !forPartner) return null;
  return (
    <SlideWrapper id="case-study-highlight" backgroundImage={deckImage('/construction-site.jpg')} overlay="gradient">
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4 text-center">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">Real Results</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading mx-auto">
            {forPartner ? 'Your Customers Can See Results Like This' : forOwner ? 'Owners and Program Teams Are Already Seeing This' : 'Contractors Like You Are Already Seeing This'}
          </h2>
          <p className="max-w-2xl text-xl text-gray-600 mx-auto">
            {forPartner ? 'Partners’ customers get time savings, productivity gains, and faster resolution. See case studies below — then explore our Skill Grid.' : forOwner ? 'From time savings to productivity and faster resolution — we show you how. Plus platform features like GC/SC consolidation and contextual graph (see What You Get).' : 'From 98% less time on close-out to $40M in recovered receivables — real numbers from the field, then we show you how.'}
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border-2 border-primary-500 bg-white p-8 shadow-lg">
            <h3 className="text-lg font-bold text-gray-900 font-heading mb-4">{(forPartner || forOwner) ? 'Time savings & use cases' : 'Six use cases, big time savings'}</h3>
            <ul className="space-y-2 text-gray-700">
              <li><span className="font-bold text-primary-500">98%</span> time reduction on job close-out</li>
              <li><span className="font-bold text-primary-500">93%</span> on contract review</li>
              <li><span className="font-bold text-primary-500">2x</span> productivity without adding headcount</li>
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-primary-500 bg-white p-8 shadow-lg">
            <h3 className="text-lg font-bold text-gray-900 font-heading mb-4">Real-world impact</h3>
            <ul className="space-y-2 text-gray-700">
              {forPartner ? (
                <>
                  <li><span className="font-bold text-primary-500">98%</span> time reduction on job close-out</li>
                  <li><span className="font-bold text-primary-500">$3M+</span> productivity / margin improvement</li>
                  <li><span className="font-bold text-primary-500">30%</span> faster RFI resolution</li>
                </>
              ) : forOwner ? (
                <>
                  <li><span className="font-bold text-primary-500">$3M+</span> productivity / margin improvement</li>
                  <li><span className="font-bold text-primary-500">30%</span> faster RFI resolution</li>
                </>
              ) : (
                <>
                  <li><span className="font-bold text-primary-500">$40M</span> revenue gains from receivables</li>
                  <li><span className="font-bold text-primary-500">$3M+</span> productivity / margin improvement</li>
                  <li><span className="font-bold text-primary-500">30%</span> faster RFI resolution</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

// Partner deck only: Partnership & Co-sell + Skill Grid CTA
export function SlidePartnershipSkillGrid() {
  return (
    <SlideWrapper id="partnership-skillgrid" backgroundImage={deckImage('/bg-digital-construction.jpg')} overlay="light">
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">Partnership & Co-sell</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl font-heading">
            Co-sell opportunities + Jobotics Skill Grid
          </h2>
          <p className="max-w-3xl text-lg leading-relaxed text-gray-600">
            We integrate with your platform; together we offer unified intelligence across construction data. Our Skill Grid shows every system we connect — PM, ERP, field, safety, documents, CRM — so you can see where we extend your reach.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-3 rounded-2xl border-2 border-primary-500 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 font-heading">Co-sell with Jobotics</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Joint value for shared customers</li>
              <li>We bring Answer & Action Engine; you bring domain and distribution</li>
              <li>Integration options: API, embedded experience, or referral</li>
            </ul>
          </div>
          <div className="flex flex-col gap-3 rounded-2xl border-2 border-primary-500 bg-primary-50 p-6 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 font-heading">Explore the Skill Grid</h3>
            <p className="text-gray-700">Construction systems we support — open APIs, MCP, and integrations. See how we fit with your stack.</p>
            <a
              href="/skill-grid"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
            >
              Open Skill Grid →
            </a>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

export function SlideCaseStudyValue() {
  return (
    <SlideWrapper id="case-study-value" backgroundImage={deckImage('/construction-site.jpg')} overlay="light">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">Case Study 1</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl font-heading">
            Value Delivered — Six Use Cases
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {caseStudyEganBenefits.map((b) => (
            <div key={b.num} className="flex flex-col rounded-xl border-2 border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 bg-primary-500 px-3 py-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-xs font-bold text-white">{b.num}</span>
                <span className="text-sm font-bold text-white">{b.title}</span>
              </div>
              <div className="flex flex-col gap-1.5 p-3">
                <p className="text-xs text-gray-700">{b.desc}</p>
                <div className="rounded-lg border border-primary-500 bg-primary-50 px-2 py-1.5">
                  <span className="text-sm font-bold text-primary-500">{b.metric}</span>
                  <p className="text-xs text-gray-600">{b.sub}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

// Case study page 2: problem we solve + real-world impact
export function SlideCaseStudyImpact({ forOwner }) {
  const businessItems = forOwner ? caseStudySylvanBusinessOwner : caseStudySylvanBusiness;
  return (
    <SlideWrapper id="case-study-impact" backgroundImage={deckImage('/construction-site.jpg')} overlay="light">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">Case Study 2</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl font-heading">
            From Fragmented Data to Real-World Impact
          </h2>
        </div>
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-bold text-gray-900 font-heading">
            {forOwner ? 'Your data is stuck in too many tools. We connect it so you can find answers without the hunt. For consolidated insights, see GC/SC data consolidation and contextual graph in What You Get.' : 'Your data is stuck in too many tools. We connect it so you can find answers without the hunt.'}
          </h3>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {caseStudySylvanProblem.map((s) => (
              <div key={s.metric} className="rounded-xl border-2 border-primary-500 bg-white p-3 text-center">
                <span className="text-xl font-bold text-primary-500 md:text-2xl">{s.metric}</span>
                <p className="mt-1 text-xs text-gray-700">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4 border-t border-gray-200 pt-6">
          <h3 className="text-base font-bold text-gray-900 font-heading">Real-world impact</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-sm font-bold text-primary-500">Productivity and Savings</span>
              {caseStudySylvanProductivity.map((p) => (
                <div key={p.metric} className="rounded-lg border border-gray-200 bg-white p-2">
                  <span className="text-base font-bold text-primary-500">{p.metric}</span>
                  <p className="text-xs text-gray-700">{p.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <span className="text-sm font-bold text-primary-500">{forOwner ? 'Real-world impact' : 'Business Outcomes'}</span>
              {businessItems.map((b) => (
                <div key={b.metric} className="rounded-lg border border-gray-200 bg-white p-2">
                  <span className="text-base font-bold text-primary-500">{b.metric}</span>
                  <p className="text-xs text-gray-700">{b.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

const tractionItems = [
  { metric: '5', label: 'Months in market', color: 'text-primary-500' },
  { metric: '2', label: 'Enterprise clients', color: 'text-construction-accent' },
  { metric: '10+', label: 'Core AI assistants', color: 'text-emerald-600' },
  { metric: '$250K', label: 'ARR', color: 'text-amber-600' },
];
const tractionItemsClient = [
  { metric: '5', label: 'Months in market', color: 'text-primary-500' },
  { metric: '2', label: 'Enterprise clients', color: 'text-construction-accent' },
  { metric: '10+', label: 'Ways to query your data', color: 'text-emerald-600' },
  { metric: '$250K', label: 'Typical engagement', color: 'text-amber-600' },
];

export function SlideTraction({ forClient }) {
  return (
    <SlideWrapper id="traction" backgroundImage={deckImage('/bg-progress.jpg')} overlay="light">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forClient ? 'Proof Points' : 'Traction'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forClient ? 'Results That Matter' : 'Tractions within '}<span className="text-primary-500">{forClient ? '' : '5 months'}</span>
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {(forClient ? tractionItemsClient : tractionItems).map((item) => (
            <div key={item.label} className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-10 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
              <span className={`text-4xl font-bold font-heading md:text-5xl ${item.color}`}>{item.metric}</span>
              <span className="text-base text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

const founders = [
  { name: 'Robert Dong', role: 'CEO & Founder', photo: 'robert-dong.png', color: 'text-primary-500', bullets: ['AI Product Leader @ TikTok, Meta and Cruise', 'Drove $B revenue via multi-modal AI', 'UC Berkeley MBA + CS Ph.D', 'AI Insights & Automation Expert'] },
  { name: 'Skye Zhou', role: 'Head of Operations', photo: 'skye-zhou.png', color: 'text-construction-accent', bullets: ['Experience across three AI startups', 'ConTech domain expertise', 'UC Berkeley + EDHEC MBA', 'Berkeley Skydeck Pad-13 Alumni'] },
  { name: 'Anita Venkiteswaran', role: 'Founding Board Member', photo: 'anita-venkiteswaran.png', color: 'text-emerald-600', bullets: ['Partner, Chief Strategist at E3 Tech', 'Seasoned Investor & Operator', 'McKinsey, Harvard MBA', 'Strong M&A and Leadership'] },
];
const advisors = [
  { name: 'Rudy Adolf', photo: 'rudy-adolf.png', title: 'Founding Partner of E-3 Tech, $7.3bn exit' },
  { name: 'Robert Metz', photo: 'robert-metz.png', title: 'Sylvan CEO, 20+ years construction experience' },
  { name: 'Joe Morrissey', photo: 'joe-morrissey.png', title: 'A16Z General Partner, ex-Segment CRO' },
  { name: 'Tom Hollands', photo: 'tom-hollands.png', title: 'A16Z Growth Partner' },
  { name: 'Michael King', photo: 'michael-king.png', title: 'A16Z GTM Partner, ex-IBM, GitHub, VMWare' },
];

export function SlideTeam({ forClient }) {
  return (
    <SlideWrapper id="team" backgroundImage={deckImage('/team-construction.jpg')} overlay="light">
      <div className="flex flex-col gap-14">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forClient ? 'Who We Are' : 'The Team'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forClient ? 'Built by construction and technology experts' : 'Built by AI Veterans'}
          </h2>
          <p className="text-lg text-gray-600">{forClient ? 'Software built for how contractors work, with deep experience in construction and field operations.' : 'Hybrid mode of AI Native + FDE'}</p>
        </div>
        <div>
          <h3 className="mb-6 text-xl font-bold text-gray-900 font-heading">Founding Team</h3>
          <div className="grid gap-6 md:grid-cols-3">
            {founders.map((f) => (
              <div key={f.name} className="flex flex-col items-center gap-5 rounded-2xl border border-gray-200 bg-white p-8 shadow-lg text-center">
                <div className="h-28 w-28 overflow-hidden rounded-full border-2 border-gray-200">
                  <img src={deckImage(`/${f.photo}`)} alt={f.name} className="h-full w-full object-cover" />
                </div>
                <div className="flex flex-col gap-1">
                  <h4 className="text-xl font-bold text-gray-900 font-heading">{f.name}</h4>
                  <span className={`text-base font-semibold ${f.color}`}>{f.role}</span>
                </div>
                <ul className="flex flex-col gap-2 text-left">
                  {f.bullets.map((b) => (
                    <li key={b} className="text-base leading-relaxed text-gray-600">• {b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-6 text-xl font-bold text-gray-900 font-heading">Advisors</h3>
          <div className="grid gap-5 grid-cols-2 md:grid-cols-5">
            {advisors.map((a) => (
              <div key={a.name} className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-white p-6 shadow-lg text-center">
                <div className="h-20 w-20 overflow-hidden rounded-full border-2 border-gray-200">
                  <img src={deckImage(`/${a.photo}`)} alt={a.name} className="h-full w-full object-cover" />
                </div>
                <h4 className="text-base font-bold text-gray-900 font-heading">{a.name}</h4>
                <p className="text-sm leading-relaxed text-gray-600">{a.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}

const features = [
  { feature: '360 Dashboard', jobotics: 'full', legacy: 'none', pointSolution: 'partial' },
  { feature: 'Multi-source AI Answer', jobotics: 'full', legacy: 'none', pointSolution: 'partial' },
  { feature: 'Voice Agent', jobotics: 'full', legacy: 'none', pointSolution: 'none' },
  { feature: '10+ AI Assistants', jobotics: 'full', legacy: 'none', pointSolution: 'partial' },
  { feature: 'ERP + PM Integration', jobotics: 'full', legacy: 'partial', pointSolution: 'partial' },
  { feature: 'Contract Review', jobotics: 'full', legacy: 'none', pointSolution: 'none' },
  { feature: 'Labor & Material Est.', jobotics: 'full', legacy: 'partial', pointSolution: 'none' },
  { feature: 'Enterprise Security', jobotics: 'full', legacy: 'full', pointSolution: 'partial' },
];
const featuresClient = [
  { feature: 'One project dashboard', jobotics: 'full', legacy: 'none', pointSolution: 'partial' },
  { feature: 'Ask across all your systems', jobotics: 'full', legacy: 'none', pointSolution: 'partial' },
  { feature: 'Voice on the jobsite', jobotics: 'full', legacy: 'none', pointSolution: 'none' },
  { feature: 'Query by topic (PM, ERP, safety, CRM)', jobotics: 'full', legacy: 'none', pointSolution: 'partial' },
  { feature: 'ERP + PM connected', jobotics: 'full', legacy: 'partial', pointSolution: 'partial' },
  { feature: 'Contract review', jobotics: 'full', legacy: 'none', pointSolution: 'none' },
  { feature: 'Labor & material estimating', jobotics: 'full', legacy: 'partial', pointSolution: 'none' },
  { feature: 'Enterprise security', jobotics: 'full', legacy: 'full', pointSolution: 'partial' },
];

function StatusIcon({ status }) {
  if (status === 'full') return <Check className="h-5 w-5 text-emerald-600" />;
  if (status === 'partial') return <Minus className="h-5 w-5 text-amber-600" />;
  return <X className="h-5 w-5 text-red-400" />;
}

export function SlideCompetitive({ forClient, forOwner, forPartner }) {
  return (
    <SlideWrapper id="competitive" backgroundImage={deckImage('/bg-modern-building.jpg')} overlay="light">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forPartner ? 'Why partner with Jobotics' : forOwner ? 'Why Jobotics for Owners' : forClient ? 'Why Jobotics' : 'Competitive Landscape'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forPartner ? 'More Capability — One Platform to Add to Your Stack' : forOwner ? 'More Capability — One Platform for Your Portfolio' : forClient ? 'More Capability — One Platform' : 'Redefining the Competitive Landscape'}
          </h2>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="p-6 text-base font-medium text-gray-600">Capability</th>
                <th className="p-6 text-base font-bold text-primary-500">Jobotics</th>
                <th className="p-6 text-base font-medium text-gray-600">Legacy PM</th>
                <th className="p-6 text-base font-medium text-gray-600">Point AI</th>
              </tr>
            </thead>
            <tbody>
              {(forOwner || forPartner || forClient ? featuresClient : features).map((f, i) => (
                <tr key={f.feature} className={`border-b border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="p-6 text-base font-medium text-gray-900">{f.feature}</td>
                  <td className="p-6"><StatusIcon status={f.jobotics} /></td>
                  <td className="p-6"><StatusIcon status={f.legacy} /></td>
                  <td className="p-6"><StatusIcon status={f.pointSolution} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SlideWrapper>
  );
}

const tiers = [
  { name: 'Bronze', criteria: 'ACV < $100M', price: '$100K', period: '/yr', highlight: false },
  { name: 'Silver', criteria: '$100M < ACV < $500M', price: '$200K', period: '/yr', highlight: true },
  { name: 'Gold', criteria: 'ACV > $500M', price: '$500K', period: '/yr', highlight: false },
];

export function SlideMarket({ forClient }) {
  return (
    <SlideWrapper id="market" backgroundImage={deckImage('/bg-data-center.jpg')} overlay="light">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">{forClient ? 'Who We Serve' : 'Go-to-Market'}</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            {forClient ? 'Built for Contractors Like You' : <>Revolutionizing Mid & Enterprise <span className="text-primary-500">MEP</span> Market</>}
          </h2>
          <p className="text-lg text-gray-600">
            {forClient ? 'Mid-size to enterprise MEP contractors — one platform for your project data, so you can ask questions and run reports without switching tools.' : 'Targeting 30,000 US mid-size MEP companies ($100M–$1B revenue) with an average ACV of $250K.'}
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`flex flex-col gap-5 rounded-2xl border p-10 transition-all hover:shadow-md ${
                tier.highlight ? 'border-primary-500 bg-primary-50 shadow-md' : 'border-gray-200 bg-white shadow-sm'
              }`}
            >
              <h3 className="text-2xl font-bold text-gray-900 font-heading">{tier.name}</h3>
              <span className="text-base font-medium text-gray-600">{tier.criteria}</span>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-gray-900 font-heading md:text-5xl">{tier.price}</span>
                <span className="text-base text-gray-600">{tier.period}</span>
              </div>
              {tier.highlight && (
                <span className="inline-flex w-fit rounded-full bg-primary-500 px-4 py-1.5 text-sm font-semibold text-white">
                  Most Popular
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

const reasons = [
  { number: '01', title: 'Massive Market', description: '$1T+ annual spend. 30K+ underserved MEP companies.', color: 'text-primary-500', borderColor: 'border-primary-200' },
  { number: '02', title: 'Proven Traction', description: '2 enterprise customers live in 5 months. $250K avg ACV.', color: 'text-construction-accent', borderColor: 'border-orange-200' },
  { number: '03', title: 'Deep Moat', description: '10+ AI assistants across ERP, PM, and field ops.', color: 'text-emerald-600', borderColor: 'border-emerald-200' },
  { number: '04', title: 'Capital Efficient', description: 'Idea to production in 1–3 months. Lean hybrid team.', color: 'text-amber-600', borderColor: 'border-amber-200' },
];

export function SlideInvestment() {
  return (
    <SlideWrapper id="investment" backgroundImage={deckImage('/closing-skyline.jpg')} overlay="gradient">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">Seed Round</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl font-heading">
            Accelerate Growth: <span className="text-primary-500">A Unique Investment Opportunity</span>
          </h2>
          <p className="text-lg text-gray-600">
            Raising our seed round to scale product and land enterprise accounts.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {reasons.map((r) => (
            <div key={r.number} className={`flex gap-6 rounded-2xl border ${r.borderColor} bg-white p-10 shadow-lg`}>
              <span className={`text-4xl font-bold ${r.color} opacity-30 font-heading`}>{r.number}</span>
              <div className="flex flex-col gap-2">
                <h3 className="text-xl font-bold text-gray-900 font-heading">{r.title}</h3>
                <p className="text-base text-gray-600">{r.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

// Client deck only: deployment timeline / milestones
const deploymentMilestones = [
  { step: 1, title: '1-hour consultation', desc: 'Qualify the opportunity of tools and data.' },
  { step: 2, title: '3-day on-site assessment', desc: 'Examine processes and workflows and determine 2–3 high-ROI use cases.' },
  { step: 3, title: 'Product dev & deploy', desc: '2–4 week deployment (on-site or remote depending on complexity).' },
  { step: 4, title: 'Expansion', desc: 'Trust built, leads to additional projects (2nd month and beyond).' },
];

export function SlideDeploymentTimeline() {
  return (
    <SlideWrapper id="deployment-timeline" backgroundImage={deckImage('/construction-site.jpg')} overlay="light">
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">How we work</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl font-heading">
            Deployment timeline & milestones
          </h2>
        </div>
        <div className="flex flex-col gap-6">
          {deploymentMilestones.map((m) => (
            <div key={m.step} className="flex items-start gap-6 rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500 text-lg font-bold text-white font-heading">
                {m.step}
              </span>
              <div className="flex flex-col gap-1">
                <h3 className="text-xl font-bold text-gray-900 font-heading">{m.title}</h3>
                <p className="text-base text-gray-600">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

// Demo videos slide — 2nd-to-last for all decks
const DEMO_VIDEOS = [
  { src: '/agentic-dashboard.mp4', title: 'Agentic Dashboard' },
  { src: '/labor-takeoff-automation.mp4', title: 'Labor Takeoff Automation' },
];

export function SlideDemoVideos() {
  return (
    <SlideWrapper id="demo-videos" backgroundImage={deckImage('/construction-site.jpg')} overlay="light">
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <p className="text-base font-semibold uppercase tracking-widest text-primary-500">Demo</p>
          <h2 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl font-heading">
            See it in action
          </h2>
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          {DEMO_VIDEOS.map((v) => (
            <div key={v.src} className="flex flex-col gap-3 rounded-2xl border-2 border-gray-200 bg-white p-4 shadow-sm overflow-hidden">
              <h3 className="text-lg font-bold text-gray-900 font-heading">{v.title}</h3>
              <video
                src={deckVideo(v.src)}
                controls
                className="w-full rounded-lg bg-black aspect-video"
                preload="metadata"
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
}

export function SlideClosing({ forClient, forOwner, forPartner }) {
  return (
    <SlideWrapper
      className="items-center justify-center text-center"
      id="closing"
      backgroundImage={deckImage('/closing-skyline.jpg')}
      overlay="gradient"
    >
      <div className="flex flex-col items-center gap-8">
        <h2 className="max-w-3xl text-5xl font-bold leading-tight tracking-tight text-gray-900 md:text-6xl lg:text-7xl font-heading">
          Thank You
        </h2>
        <div className="h-1 w-24 rounded-full bg-primary-500" />
        <p className="max-w-md text-xl leading-relaxed text-gray-600">
          {forPartner ? "Let's talk partnership — co-sell, integration, and how we extend your platform." : forOwner ? 'Let us show you how one platform can give you visibility and control across your capital program.' : forClient ? 'Let us show you how one platform can connect your data and save your team time.' : 'Let us show you unified AI in production.'}
        </p>
        <div className="mt-4 flex flex-col items-center gap-4 text-base">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            <div className="flex flex-col items-center gap-1">
              <a href="mailto:robert.dong@jobotics.ai" className="font-semibold text-primary-600 hover:underline">robert.dong@jobotics.ai</a>
              <a href="https://calendly.com/robert-dong-jobotics/30min" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">Book a meeting</a>
            </div>
            <div className="flex flex-col items-center gap-1">
              <a href="mailto:skye@jobotics.ai" className="font-semibold text-primary-600 hover:underline">skye@jobotics.ai</a>
              <a href="https://calendly.com/skye-jobotics/30min" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">Book a meeting</a>
            </div>
          </div>
          <span className="font-semibold text-gray-900">jobotics.ai</span>
          <span className="text-gray-600">Confidential</span>
        </div>
      </div>
    </SlideWrapper>
  );
}

export function DeckSlides() {
  return (
    <>
      <SlideTitle />
      <SlideOpportunity />
      <SlideProblem />
      <SlideSolution />
      <SlideValue />
      <SlideCaseStudyValue />
      <SlideCaseStudyImpact />
      <SlideTraction />
      <SlideTeam />
      <SlideCompetitive />
      <SlideMarket />
      <SlideInvestment />
      <SlideDemoVideos />
      <SlideClosing />
    </>
  );
}
